import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Camera, CameraOff, Download, Upload, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import toast from 'react-hot-toast';
import { applyBiometricCentering } from '../lib/biometricCentering';

   interface WebcamCaptureProps {
    studentId: string | number;
    currentPhoto?: string;
    onPhotoUpdated: () => void;
    studentData?: {
      Name?: string;
      Vorname?: string;
      email?: string;
      RFID?: string;
      Diff_D?: string;
      Diff_M?: string;
      Diff_E?: string;
    };
    onStudentDataUpdated?: () => void;
  }

 export default function WebcamCapture({ studentId, currentPhoto, onPhotoUpdated, studentData, onStudentDataUpdated }: WebcamCaptureProps) {
  const [cameraOn, setCameraOn] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [availableCams, setAvailableCams] = useState<MediaDeviceInfo[]>([]);
  const [selectedCam, setSelectedCam] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState<string | null>(currentPhoto || null);
  const [newPhoto, setNewPhoto] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [showCamList, setShowCamList] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load available cameras on mount (without requesting permission)
  useEffect(() => {
    const loadCameras = async () => {
      try {
        // Only enumerate devices without requesting permission
        const devices = await navigator.mediaDevices.enumerateDevices();
        const cams = devices.filter(device => device.kind === 'videoinput');
        console.log('[CAM] Available cameras:', cams.length);
        
        setAvailableCams(cams);
        if (cams.length > 0) {
          setSelectedCam(cams[0].deviceId);
          console.log('[CAM] Selected camera:', cams[0].label || cams[0].deviceId);
        }
      } catch (err) {
        console.error('[CAM ENUM ERROR]', err);
        toast.error('Kamera-Geräte konnten nicht geladen werden');
      }
    };

    loadCameras();
  }, []);

  // Update current photo when props change
  useEffect(() => {
    setCurrentPhotoUrl(currentPhoto || null);
    // Clear photo preview when student changes
    setPhotoPreview(null);
    setNewPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [currentPhoto, studentId]);

  // Handle camera stream
  useEffect(() => {
    if (!cameraOn) return;

    const startCamera = async () => {
      try {
        // Stop any existing stream first
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }

        const constraints = selectedCam 
          ? { video: { deviceId: { exact: selectedCam } } }
          : { video: true };

        console.log('[CAM] Starting camera with constraints:', constraints);
        
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
        setStream(mediaStream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          console.log('[CAM] Video stream set to video element');
        }
      } catch (err) {
        console.error('[CAM STREAM ERROR]', err);
        setCameraOn(false);
        toast.error('Kamera konnte nicht gestartet werden');
      }
    };

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraOn, selectedCam]);

  // Handle video ready event
  const handleVideoReady = useCallback(() => {
    console.log('[CAM] Video ready event fired');
    setVideoReady(true);
  }, []);

  // Toggle camera on/off
  const toggleCamera = () => {
    console.log('[CAM] Toggle camera, current state:', cameraOn);
    
    if (cameraOn) {
      console.log('[CAM] Turning camera OFF');
      stream?.getTracks().forEach(t => t.stop());
      setCameraOn(false);
      setVideoReady(false);
      if (videoRef.current) videoRef.current.srcObject = null;
    } else {
      console.log('[CAM] Turning camera ON');
      setCameraOn(true);
    }
  };

  // Take snapshot
  const takeSnapshot = async () => {
    console.log('[CAM] takeSnapshot called, videoReady:', videoReady);
    if (!videoRef.current || !canvasRef.current || !videoReady) {
      console.log('[CAM] takeSnapshot early return - videoRef:', !!videoRef.current, 'canvasRef:', !!canvasRef.current, 'videoReady:', videoReady);
      return;
    }
    
    setLoading(true);
    const processingToast = toast.loading('Foto wird aufgenommen und zentriert...');
    
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        toast.error('Canvas-Kontext nicht verfügbar', { id: processingToast });
        setLoading(false);
        return;
      }

      console.log('[CAM] Drawing image to canvas...');
      ctx.drawImage(video, 0, 0);
      
      // Erstelle initiales Blob vom Canvas
      const initialBlob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.8);
      });

      if (!initialBlob) {
        toast.error('Fehler beim Erstellen des Fotos', { id: processingToast });
        setLoading(false);
        return;
      }

      console.log('[CAM] Initial blob created, size:', initialBlob.size);
      console.log('[CAM] Applying biometric centering...');
      
      // Wende biometrische Zentrierung an
      let processedBlob: Blob;
      try {
        processedBlob = await applyBiometricCentering(initialBlob);
        console.log('[CAM] ✅ Biometric centering completed', {
          originalSize: initialBlob.size,
          processedSize: processedBlob.size,
          sizeChanged: initialBlob.size !== processedBlob.size
        });
        
        // Verifiziere, dass das Blob tatsächlich unterschiedlich ist
        if (initialBlob.size === processedBlob.size) {
          console.warn('[CAM] ⚠️ Warnung: Verarbeitetes Blob hat die gleiche Größe wie das Original');
        }
      } catch (centeringError) {
        console.error('[CAM] ❌ Biometric centering failed:', centeringError);
        // Falls Zentrierung fehlschlägt, verwende das Original
        processedBlob = initialBlob;
        toast('Gesichtserkennung fehlgeschlagen, Originalbild wird verwendet', { 
          icon: '⚠️',
          id: processingToast,
          duration: 3000
        });
      }

      // Erstelle neue URL für das verarbeitete Bild
      // WICHTIG: Revoke alte URL falls vorhanden, um Memory Leaks zu vermeiden
      if (photoPreview) {
        URL.revokeObjectURL(photoPreview);
      }
      
      const imageURL = URL.createObjectURL(processedBlob);
      const file = new File([processedBlob], 'snapshot.jpg', { type: 'image/jpeg' });

      console.log('[CAM] Setting photoPreview and newPhoto...', {
        imageURL: imageURL.substring(0, 50) + '...',
        fileSize: file.size,
        fileType: file.type
      });
      
      setPhotoPreview(imageURL);
      setNewPhoto(file);
      
      // Stop camera
      if (videoRef.current?.srcObject) {
        (videoRef.current.srcObject as MediaStream)
          .getTracks()
          .forEach(track => track.stop());
        videoRef.current.srcObject = null;
      }

      setCameraOn(false);
      setVideoReady(false);
      toast.success('Foto aufgenommen und zentriert', { id: processingToast });
      console.log('[CAM] Photo taken and centered successfully');
    } catch (error) {
      console.error('[CAM] Error taking snapshot:', error);
      toast.error('Fehler beim Aufnehmen des Fotos', { id: processingToast });
    } finally {
      setLoading(false);
    }
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[CAM] handleFileUpload called');
    const file = event.target.files?.[0];
    console.log('[CAM] Selected file:', file);
    if (file && file.type.startsWith('image/')) {
      console.log('[CAM] Valid image file, applying biometric centering...');
      
      setLoading(true);
      const processingToast = toast.loading('Bild wird geladen und zentriert...');
      
      try {
        // Wende biometrische Zentrierung auch beim Laden an
        const processedBlob = await applyBiometricCentering(file);
        
        console.log('[CAM] ✅ File centering completed', {
          originalSize: file.size,
          processedSize: processedBlob.size
        });
        
        // Revoke alte URL falls vorhanden
        if (photoPreview) {
          URL.revokeObjectURL(photoPreview);
        }
        
        const imageURL = URL.createObjectURL(processedBlob);
        const newFile = new File([processedBlob], file.name, { type: 'image/jpeg' });
        
        setPhotoPreview(imageURL);
        setNewPhoto(newFile);
        toast.success('Foto geladen und zentriert', { id: processingToast });
        console.log('[CAM] Photo loaded and centered successfully');
      } catch (error) {
        console.error('[CAM] Error centering uploaded file:', error);
        // Bei Fehler: Original verwenden
        setPhotoPreview(URL.createObjectURL(file));
        setNewPhoto(file);
        toast('Gesichtserkennung fehlgeschlagen, Originalbild wird verwendet', { 
          icon: '⚠️',
          id: processingToast,
          duration: 3000
        });
      } finally {
        setLoading(false);
      }
    } else {
      console.error('[CAM] Invalid file type:', file?.type);
      toast.error('Bitte wählen Sie eine Bilddatei aus');
    }
  };

         // Save photo and student data to backend
   const savePhoto = async () => {
     if (!studentId || studentId === 0 || studentId === '0') {
       toast.error('Bitte wählen Sie zuerst einen Schüler aus');
       return;
     }

     setLoading(true);
     try {
               // Save student data if provided
        if (studentData && onStudentDataUpdated) {
          try {
            const response = await api.put(`/students/${studentId}`, studentData);
            toast.success('Schülerdaten erfolgreich gespeichert');
            // Call the callback to trigger data refresh
            onStudentDataUpdated();
          } catch (error: any) {
            console.error('Error saving student data:', error);
            
            // Handle permission errors specifically
            if (error?.response?.status === 403) {
              const message = error?.response?.data?.message || 'Keine Berechtigung zum Bearbeiten dieses Schülers'
              toast.error(
                <div>
                  <div className="font-semibold">Zugriff verweigert</div>
                  <div className="text-sm mt-1">{message}</div>
                </div>,
                { duration: 6000 }
              )
            } else if (error?.response?.status === 401) {
              toast.error('Bitte melden Sie sich erneut an')
            } else {
              const message = error?.response?.data?.message || 'Fehler beim Speichern der Schülerdaten'
              toast.error(message)
            }
          }
        }

       // Save photo if available
       if (newPhoto || photoPreview) {
         const formData = new FormData();
         
         // Use newPhoto if available, otherwise create a file from photoPreview
         let photoFile = newPhoto;
         if (!photoFile && photoPreview) {
           // Convert photoPreview URL to File object
           const response = await fetch(photoPreview);
           const blob = await response.blob();
           photoFile = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
         }
         
         if (photoFile) {
           formData.append('photo', photoFile);
         }

         console.log('[CAM] Uploading photo for student ID:', studentId);
         
         await api.put(`/students/${studentId}/photo`, formData, {
           headers: {
             'Content-Type': 'multipart/form-data',
           },
         });

         toast.success('Foto erfolgreich gespeichert');
         onPhotoUpdated();
         setNewPhoto(null);
         setPhotoPreview(null);
       }
     } catch (error: any) {
       console.error('Error saving photo:', error);
       if (error.response?.status === 404) {
         toast.error('Schüler nicht gefunden');
       } else if (error.response?.status === 500) {
         toast.error('Server-Fehler beim Speichern');
       } else {
         toast.error('Fehler beim Speichern');
       }
     } finally {
       setLoading(false);
     }
   };

  // Delete photo preview
  const deletePhoto = () => {
    setPhotoPreview(null);
    setNewPhoto(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Foto Vorschau gelöscht');
  };

  // Load photo from file
  const loadPhoto = () => {
    fileInputRef.current?.click();
  };


  
  
  
  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900 dark:shadow-black/30">
      <div className="border-b border-gray-200 p-4 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Webcam</h3>
      </div>
      <div className="p-4">
                 {/* Preview Area */}
         <div className="relative mb-4 flex h-48 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
           {photoPreview ? (
             <img
               src={photoPreview}
               alt="Neues Foto Vorschau"
               className="max-h-full max-w-full rounded object-contain"
             />
                       ) : cameraOn ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                onLoadedData={handleVideoReady}
                onCanPlay={() => console.log('[CAM] Video can play')}
                onError={(e) => console.error('[CAM] Video error:', e)}
                className="max-h-full max-w-full rounded object-contain"
              />
           ) : currentPhotoUrl ? (
             <img
               src={currentPhotoUrl}
               alt="Aktuelles Schülerfoto"
               className="max-h-full max-w-full rounded object-contain"
             />
                       ) : (
             <div className="text-center">
               <CameraOff className="mx-auto mb-2 h-12 w-12 text-gray-400 dark:text-gray-500" />
               <p className="text-sm text-gray-500 dark:text-gray-400">
                  {!studentId || studentId === 0 || studentId === '0' ? 'Schüler auswählen' : 'Webcam inaktiv'}
                </p>
              </div>
            )}
         </div>

        {/* Camera Controls */}
        <div className="flex flex-wrap gap-2 mb-4">
          {/* Camera Toggle */}
          <div className="relative">
            <button
              onClick={toggleCamera}
              className="btn btn-sm btn-outline flex items-center"
              disabled={loading}
            >
              {cameraOn ? <CameraOff className="h-4 w-4 mr-1" /> : <Camera className="h-4 w-4 mr-1" />}
              {cameraOn ? 'OFF' : 'ON'}
            </button>
            
            {/* Camera Selection Dropdown */}
            {availableCams.length > 1 && (
              <div className="relative">
                <button
                  onClick={() => setShowCamList(!showCamList)}
                  className="btn btn-sm btn-outline ml-1"
                >
                  ▼
                </button>
                {showCamList && (
                  <div className="absolute left-0 top-full z-10 mt-1 min-w-48 rounded border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-900">
                    {availableCams.map((cam) => (
                      <button
                        key={cam.deviceId}
                        onClick={() => {
                          setSelectedCam(cam.deviceId);
                          setShowCamList(false);
                        }}
                        className={`block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800 ${
                          selectedCam === cam.deviceId ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-200' : ''
                        }`}
                      >
                        {cam.label || `Kamera ${cam.deviceId.slice(0, 8)}...`}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Take Photo */}
          <button
            onClick={takeSnapshot}
            className="btn btn-sm btn-outline"
            disabled={!cameraOn || !videoReady || loading}
          >
            TAKE
          </button>

          {/* Load Photo */}
          <button
            onClick={loadPhoto}
            className="btn btn-sm btn-outline"
            disabled={loading}
          >
            <Upload className="h-4 w-4 mr-1" />
            LOAD
          </button>

          {/* Delete Photo */}
          <button
            onClick={deletePhoto}
            className="btn btn-sm btn-outline"
            disabled={!photoPreview || loading}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            DEL
          </button>
        </div>

                                                                                                                                           {/* Save Button */}
          <button
            onClick={savePhoto}
            className={`w-full ${
              !studentId || studentId === 0 || studentId === '0'
                ? 'btn btn-outline opacity-50 cursor-not-allowed' 
                : 'btn btn-primary'
            }`}
            disabled={loading || !studentId || studentId === 0 || studentId === '0'}
          >
            {loading ? 'Speichere...' : 
             (!studentId || studentId === 0 || studentId === '0') ? 'Schüler auswählen' : 'Daten & Foto speichern'}
          </button>
         


        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Hidden canvas for photo capture */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
}
